import { IconClose } from "@/assets/icons";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import {
  INPUT_MAX_LENGTH_MESSAGE,
  INPUT_MAX_NUMBER_MESSAGE,
  INPUT_NEGATIVE_NUMBER_MESSAGE,
  MAX_CHARACTER_INPUT_VALIDATION,
  MAXIMUM_NUMBER_VALIDATION,
} from "@/constants/common";
import { MIN_YEAR_LIMIT, MAX_YEAR_LIMIT } from "@/constants/datetime";
import { INVALID_DATE, INVALID_DATE_LIMIT_MAX, INVALID_DATE_LIMIT_MIN } from "@/constants/invalids";
import { blue } from "@/theme/colors";
import { TOption } from "@/types/common";
import { dayjs } from "@/utils/dayjs";
import { Dayjs } from "dayjs";
import { Box, FormHelperText, IconButton, InputAdornment, TextField } from "@mui/material";
import { ChangeEvent, memo, useState, useCallback } from "react";

const EDIT_CELL_HEIGHT = 33;
type TPayerSelectCellProps = {
  value: number | null;
  options: TOption[];
  isEditing: boolean;
  displayName: string | null;
  onChange: (value: number | null, label: string | null) => void;
};
export const FeePayerSelectCell = memo(
  ({ value, options, isEditing, displayName, onChange }: TPayerSelectCellProps) => {
    if (!isEditing) {
      return (
        <Box component="span" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {displayName}
        </Box>
      );
    }
    const handleChange = (next: TOption["value"]) => {
      const nextValue = next ? Number(next) : null;
      const label = options.find((opt) => String(opt.value) === String(next))?.label ?? null;
      onChange(nextValue, label);
    };
    return (
      <Box sx={{ display: "flex", alignItems: "center", minHeight: EDIT_CELL_HEIGHT, width: "100%", "& > .MuiBox-root": { width: "100%" } }}>
        <Select
          nonControl
          size="md"
          placeholder="選択してください"
          options={options}
          value={value != null ? String(value) : ""}
          onChange={handleChange}
          sx={{ width: "100%" }}
        />
      </Box>
    );
  },
  (prev, next) =>
    prev.value === next.value && prev.isEditing === next.isEditing && prev.displayName === next.displayName && prev.options === next.options,
);

type TDateCellProps = {
  value: string | null;
  isEditing: boolean;
  showValidationError?: boolean;
  onChange: (value: string | null) => void;
};

export const getFeeDateErrorMessage = (value: string | null, label?: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const replacePath = (template: string) => {
    if (!label) {
      return template.replace("「{{path}}」の", "").replace("「{{path}}」は", "").replace("「{{path}}」", "").trim();
    }
    return template.replace("{{path}}", label);
  };
  if (value === "Invalid Date") {
    return replacePath(INVALID_DATE);
  }
  const date = dayjs(value);
  if (!date.isValid()) {
    return replacePath(INVALID_DATE);
  }
  const year = date.year();
  if (year < MIN_YEAR_LIMIT) {
    return replacePath(INVALID_DATE_LIMIT_MIN);
  }
  if (year > MAX_YEAR_LIMIT) {
    return replacePath(INVALID_DATE_LIMIT_MAX);
  }
  return undefined;
};

export const isFeeDateInvalid = (value: string | null): boolean => {
  return getFeeDateErrorMessage(value) !== undefined;
};

export const FeeDateCell = memo(
  ({ value, isEditing, showValidationError = false, onChange }: TDateCellProps) => {
    const [isTouched, setIsTouched] = useState(false);
    const label = "徴収年月日";
    const rawError = getFeeDateErrorMessage(value, label);
    const showError = rawError && (value === "Invalid Date" || isTouched || showValidationError);
    const errorMessage = showError ? rawError : undefined;

    const handleChange = useCallback((next: Dayjs | null) => {
      setIsTouched(true);
      onChange(next ? next.format("YYYY-MM-DD") : null);
    }, [onChange]);

    const handleBlur = useCallback(() => {
      setIsTouched(true);
    }, []);

    if (!isEditing) {
        return <span>{value ? dayjs(value).format("YYYY/MM/DD") : ""}</span>;
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: EDIT_CELL_HEIGHT, position: "relative", width: "100%" }}>
        <InputDatePicker
          nonControl
          placeholder="選択してください"
          value={value ? dayjs(value) : null}
          error={Boolean(errorMessage)}
          onChange={handleChange}
          onBlur={handleBlur}
          disableHelperText
        />
        {Boolean(errorMessage) && (
          <FormHelperText
            error
            sx={{
              marginTop: "4px",
              marginLeft: "4px",
              lineHeight: 1.2,
              whiteSpace: "pre-line",
            }}
          >
            {errorMessage}
          </FormHelperText>
        )}
      </Box>
    );
  },
  (prev, next) => prev.value === next.value && prev.isEditing === next.isEditing && prev.showValidationError === next.showValidationError,
);

type TTextCellProps = {
  value: string;
  isEditing: boolean;
  defaultValue?: string;
  onChange: (value: string) => void;
};

export const FeeTextCell = memo(
  ({ value, isEditing, onChange }: TTextCellProps) => {
    const isOverLimit = value.length > MAX_CHARACTER_INPUT_VALIDATION;
    if (!isEditing) {
      return (
        <Box component="span" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {value}
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", minHeight: EDIT_CELL_HEIGHT, position: "relative" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="入力内容"
          value={value}
          error={isOverLimit}
          helperText={isOverLimit ? INPUT_MAX_LENGTH_MESSAGE(MAX_CHARACTER_INPUT_VALIDATION) : undefined}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": { height: EDIT_CELL_HEIGHT, alignItems: "center" },
            "& .MuiOutlinedInput-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": { borderColor: blue[70] },
            "& .MuiOutlinedInput-input": { paddingTop: 0, paddingBottom: 0, height: "100%", lineHeight: `${EDIT_CELL_HEIGHT}px` },
            "& .MuiFormHelperText-root": { position: "absolute", top: "100%", left: 0, marginTop: 0, marginLeft: "4px", lineHeight: 1.2 },
          }}
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" onClick={() => onChange("")}>
                  <IconClose width={16} height={16} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
    );
  },
  (prev, next) => prev.value === next.value && prev.isEditing === next.isEditing,
);

type TNumberCellProps = {
    value: string;
    isEditing: boolean;
    onChange: (value: string) => void;
};

export const MAX_FEE_AMOUNT_DIGITS = String(MAXIMUM_NUMBER_VALIDATION).length;

export const isFeeAmountInvalid = (value: string): boolean => {
    if (value === "") {
        return false;
    }
    if (!/^\d+$/.test(value)) {
        return true;
    }
    return value.replace(/^0+(?=\d)/, "").length > MAX_FEE_AMOUNT_DIGITS;
};

// Display-only thousands separator for 手数料の額; storage stays a plain integer string.
const formatFeeAmount = (value: string): string => (/^\d+$/.test(value) ? Number(value).toLocaleString("en-US") : value);

export const FeeNumberCell = memo(({ value, isEditing, onChange }: TNumberCellProps) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value);
    const isNegative = value.includes("-");
    const isOverMax = isFeeAmountInvalid(value) && !isNegative;
    const errorMessage = isNegative ? INPUT_NEGATIVE_NUMBER_MESSAGE : isOverMax ? INPUT_MAX_NUMBER_MESSAGE(MAXIMUM_NUMBER_VALIDATION) : undefined;
    if (!isEditing) {
        return <span>{formatFeeAmount(value)}</span>;
    }
    return (
        <Box sx={{ display: "flex", alignItems: "center", minHeight: EDIT_CELL_HEIGHT, position: "relative" }}>
            <TextField
                fullWidth
                size="small"
                type="text"
                inputMode="numeric"
                placeholder="紹介手数料"
                value={value}
                error={Boolean(errorMessage)}
                helperText={errorMessage}
                onChange={handleChange}
                inputProps={{ inputMode: "numeric" }}
                sx={{
                    "& .MuiOutlinedInput-root": { height: EDIT_CELL_HEIGHT, alignItems: "center" },
                    "& .MuiOutlinedInput-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": { borderColor: blue[70] },
                    "& .MuiOutlinedInput-input": { paddingTop: 0, paddingBottom: 0, height: "100%", lineHeight: `${EDIT_CELL_HEIGHT}px` },
                    "& .MuiFormHelperText-root": { marginTop: "4px", marginLeft: "4px", lineHeight: 1.2, whiteSpace: "nowrap" },
                }}
            />
        </Box>
    );
  },
  (prev, next) => prev.value === next.value && prev.isEditing === next.isEditing,
);
